import {acceptHMRUpdate, defineStore} from "pinia";
import {PLANNING_ASSISTANT} from "@/ai-assistant/constants";
import {
    AssistantHistoryTurn,
    AssistantMessage,
    AssistantRole,
    AssistantScreen,
    Proposition
} from "@/ai-assistant/interfaces";

interface AiAssistantState {
    isOpen: boolean;
    /**
     * Folded down to a pill: the window is out of the way, the conversation
     * is still running. Only meaningful while open.
     */
    minimized: boolean;
    assistantKey: string;
    settingsExpanded: boolean;
    historyExpanded: boolean;
    messages: AssistantMessage[];
    isStreaming: boolean;
    error: string | null;
    /**
     * Where the pharmacist stands, as the mounted screen describes it.
     *
     * Held here rather than passed down because the drawer is mounted once in
     * the application layout, above every screen: it has no other way of
     * knowing which week is on display behind it.
     */
    screen: AssistantScreen | null;
    /** The screen that declared the context, so only it may take it back. */
    screenOwner: number | null;
    /**
     * A question put in the composer without being sent.
     *
     * « Demander un ajustement » writes here rather than asking on the
     * pharmacist's behalf: nothing leaves without their click, including a
     * question about their own team.
     */
    draft: string;
    /**
     * How many times the pharmacist asked to see the answers whole.
     *
     * An answer is written at reading pace; a click on it, on the composer or
     * on send says the pace is no longer wanted. Counted rather than toggled,
     * so every request is seen even when two come in the same tick.
     */
    revealRequests: number;
}

const messageId = (): string => `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

/**
 * The state of the assistant drawer, held above the components so the
 * conversation outlives them.
 *
 * Nothing is archived in this version, and that is exactly what this store
 * expresses: the transcript survives closing the drawer and moving between
 * screens, and dies with the application. Were it held in the drawer
 * component, a pharmacist closing the panel to look at the planning would
 * lose the answer they had just asked for.
 */
export const useAiAssistantStore = defineStore('ai-assistant', {
    state: (): AiAssistantState => ({
        isOpen: false,
        minimized: false,
        assistantKey: PLANNING_ASSISTANT,
        settingsExpanded: false,
        historyExpanded: false,
        messages: [],
        isStreaming: false,
        error: null,
        screen: null,
        screenOwner: null,
        draft: '',
        revealRequests: 0
    }),
    actions: {
        /**
         * Open the drawer on an assistant.
         *
         * Switching assistant clears the transcript: an exchange about the
         * team is not a valid premise for another assistant's answers.
         *
         * @param assistantKey
         */
        open(assistantKey: string = PLANNING_ASSISTANT) {
            if (assistantKey !== this.assistantKey) {
                this.assistantKey = assistantKey;
                this.clear();
            }

            this.isOpen = true;
            this.minimized = false;
        },

        /**
         * Close the drawer, keeping the conversation.
         *
         * A request still in flight is the caller's to abort — the composable
         * owns the controller.
         */
        close() {
            this.isOpen = false;
            this.minimized = false;
            this.settingsExpanded = false;
            this.historyExpanded = false;
        },

        /**
         * Fold the window down to its pill, an answer being written included.
         */
        minimize() {
            this.minimized = true;
        },

        /**
         * What the launcher does: hide the window when it is in view, bring it
         * back otherwise — from closed as from folded down.
         *
         * @param assistantKey
         */
        toggle(assistantKey: string = PLANNING_ASSISTANT) {
            if (this.isOpen && !this.minimized && assistantKey === this.assistantKey) {
                this.close();
            } else {
                this.open(assistantKey);
            }
        },

        /**
         * One panel at a time: both sit above the conversation, and the drawer
         * is narrow enough that stacking them would push it out of sight.
         */
        toggleSettings(expanded?: boolean) {
            this.settingsExpanded = expanded ?? !this.settingsExpanded;

            if (this.settingsExpanded) {
                this.historyExpanded = false;
            }
        },

        toggleHistory(expanded?: boolean) {
            this.historyExpanded = expanded ?? !this.historyExpanded;

            if (this.historyExpanded) {
                this.settingsExpanded = false;
            }
        },

        /**
         * What the mounted screen has in front of the pharmacist.
         *
         * @param owner
         * @param screen
         */
        setScreen(owner: number, screen: AssistantScreen | null) {
            this.screen = screen;
            this.screenOwner = owner;
        },

        /**
         * Give the context back on leaving a screen, unless another has taken
         * it in the meantime.
         *
         * @param owner
         */
        releaseScreen(owner: number) {
            if (this.screenOwner === owner) {
                this.screen = null;
                this.screenOwner = null;
            }
        },

        /**
         * Append a turn and return its id, which is how the streamed answer
         * finds the message to fill in.
         *
         * @param role
         * @param content
         * @param isStreaming
         * @param screen The screen it was asked from, kept with the turn.
         */
        appendMessage(
            role: AssistantRole,
            content = '',
            isStreaming = false,
            screen: AssistantScreen | null = null
        ): string {
            const id = messageId();

            this.messages.push({id, role, content, timestamp: new Date(), isStreaming, screen});

            return id;
        },

        /**
         * Append streamed text to one message, addressed by id.
         *
         * By id and not by position: a stream that outlives a `clear()` must
         * write nowhere rather than into whichever message now sits last.
         *
         * @param id
         * @param chunk
         */
        appendToMessage(id: string, chunk: string) {
            const message = this.messages.find(candidate => candidate.id === id);

            if (message) {
                message.content += chunk;
            }
        },

        /**
         * Mark a message as no longer being written.
         *
         * @param id
         */
        finishMessage(id: string) {
            const message = this.messages.find(candidate => candidate.id === id);

            if (message) {
                message.isStreaming = false;
            }
        },

        /**
         * Remove a message, used to drop an answer that never came.
         *
         * @param id
         */
        removeMessage(id: string) {
            this.messages = this.messages.filter(candidate => candidate.id !== id);
        },

        /**
         * Say that an answer carries scenarios, before they have arrived.
         *
         * Announced first so the answer does not read as complete while its
         * scenarios are still on their way — an answer that mentions a
         * proposition and shows none looks like a proposition that failed.
         *
         * @param id
         * @param loading
         */
        expectPropositions(id: string, loading = true) {
            const message = this.messages.find(candidate => candidate.id === id);

            if (message) {
                message.propositionsLoading = loading;
            }
        },

        /**
         * Attach the scenarios of one answer, addressed by id like its text.
         *
         * A shortfall is held on the message rather than in the panel's error
         * banner: it concerns this answer and nothing else, so it must not
         * outlive it — a pharmacist who asked another question, or cleared the
         * conversation, should not be told about scenarios that belonged to an
         * exchange they have left.
         *
         * @param id
         * @param propositions
         * @param shortfall
         */
        attachPropositions(id: string, propositions: Proposition[], shortfall: string | null = null) {
            const message = this.messages.find(candidate => candidate.id === id);

            if (message) {
                message.propositions = propositions;
                message.propositionsError = shortfall;
                message.propositionsLoading = false;
            }
        },

        /**
         * Put a decided scenario back where it was read.
         *
         * Addressed by identifier rather than by position: the server returns
         * the whole object after a decision — new state, frozen verdict — and
         * it has to land on the card the pharmacist clicked, whatever the
         * conversation did meanwhile.
         *
         * @param proposition
         */
        replaceProposition(proposition: Proposition) {
            this.messages.forEach(message => {
                const index = message.propositions?.findIndex(candidate => candidate.id === proposition.id) ?? -1;

                if (index >= 0 && message.propositions) {
                    message.propositions[index] = proposition;
                }
            });
        },

        setDraft(draft: string) {
            this.draft = draft;
        },

        /**
         * Show every answer whole, now: the writing pace is a comfort, never
         * a wait imposed on someone who has moved on.
         */
        revealAnswers() {
            this.revealRequests++;
        },

        setError(error: string | null) {
            this.error = error;
        },

        /**
         * The turns that travel with the next question, oldest first.
         *
         * A turn still being written is left out: half an answer is not a
         * premise worth sending back.
         *
         * @param limit
         */
        historyTurns(limit: number): AssistantHistoryTurn[] {
            return this.messages
                .filter(message => !message.isStreaming && message.content.length > 0)
                .slice(-limit)
                // The scenarios stay behind: they are already in the
                // conversation the server holds, and re-sending their figures
                // as prose would let the model quote them as its own.
                .map(({role, content}) => ({role, content}));
        },

        clear() {
            this.messages = [];
            this.error = null;
            this.draft = '';
        }
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAiAssistantStore, import.meta.hot));
}
